/**
 * Where `terrain-relief.ts`'s inputs come from — the measuring half, kept
 * separate from the surface itself for the reason enhanced-models.md §2.1
 * gives: this is the half that can be silently wrong in ways a picture will
 * not show, so it is plain arithmetic over real data with tests against it.
 *
 * Three things are read out of a region, and all three are shipped data:
 *
 * 1. **Watercourses.** Every face painted `BARON.PAL` index 4 (world-terrain.md
 *    §7.2, VERIFIED: rivers render at `colourFront` 4 `#304965`). A river is
 *    the bottom of a valley, so these are what the relief carves down to.
 * 2. **The ten hills.** Any placed object that reaches more than
 *    `HILL_MIN_HEIGHT_M` above its own base — in the shipped corpus that is
 *    exactly the three conical landforms of gi-format.md §9 and nothing else.
 *    Their footprints become flat zones, so a hill still rises from the datum
 *    with its authored geometry and its own `.GI` collision surface intact.
 * 3. **Aerodromes**, handed in already anchored onto real ground by
 *    `world/region-ground.ts`. They become graded aprons — and, through
 *    `withLandingFields`, prepared ground an undercarriage survives. They are
 *    *not* re-projected here: this function used to call `projectAerodromes`
 *    itself and stamp a landable strip at every fitted position with no land
 *    check at all, which for London would have graded four aerodromes 2-22 km
 *    out in the North Sea (docs/engineering/london-sea-placements.md §3.1).
 *    A wet site is now dropped rather than graded.
 *
 * ## The sea, and why coastal regions are excluded
 *
 * London and Dunkirk each carry one `scaleExponent = 10` water plate — 378 x 245
 * km and 291 x 237 km, `BARON.PAL` index 4 — that is the North Sea and the
 * Channel. Two consequences:
 *
 * - Those faces must not be read as rivers. `SEA_SCALE_EXPONENT` excludes them,
 *   or a single triangle 23 000 km² across would drag a valley over the whole
 *   map.
 * - Relief itself cannot ship in those regions until there is a land/sea mask,
 *   because rolling ground under a flat sea plate surfaces as islands. Until
 *   that mask exists `reliefSourceFor` returns `undefined` for them and they
 *   stay exactly as flat as they are today. Stated as a gap rather than
 *   papered over — see docs/engineering/enhanced-terrain.md.
 */

import { bestDrawableLod, type BakedModelBody, type BakedModelLodBody } from "../three/baked-model.ts";
import { dryShare, NO_WATER, AERODROME_GROUND_RADIUS_M, type GroundSite, type WaterTest } from "./aerodrome-projection.ts";
import { placementWorldTransform, type PlacementOrigin } from "./placement-transform.ts";
import type { WorldRegionBody } from "./region-loader.ts";
import { SEA_SCALE_EXPONENT } from "./water-mask.ts";
import {
  AERODROME_FLAT_M,
  DEFAULT_RELIEF,
  type FlatZone,
  type ReliefOptions,
  type RiverSegment,
} from "@red-opus/engine";

/** `colourFront` of every river, canal and sea face in the corpus (world-terrain.md §7.2). */
export const WATER_PALETTE_INDEX = 4;

/**
 * `BARON.PAL[4]` = `#304965`, expanded. Both triangle and line faces carry
 * resolved RGB rather than the index, so the test is on the colour — which is
 * exact, because a baked byte is the palette entry verbatim
 * (`three/webgpu-support.ts` disables colour management app-wide).
 */
const WATER_RGB: readonly [number, number, number] = [48, 73, 101];

function isWaterColour(rgba: readonly number[]): boolean {
  return rgba[0] === WATER_RGB[0] && rgba[1] === WATER_RGB[1] && rgba[2] === WATER_RGB[2];
}

export { SEA_SCALE_EXPONENT } from "./water-mask.ts";

/**
 * Above this, a placed object is one of the ten landforms rather than a
 * building. Re-measured over the whole placed corpus against the current bake
 * (`unitsPerMetreForScale`, docs/engineering/london-sea-placements.md §8, which
 * made every `scaleExponent > 0` object 2.4765× larger): ordinary buildings top
 * out at **76.2 m** (`TERR3-34`/`TERR5-33`/`TERR4-88`, the same church that used
 * to measure 30.8 m), the tallest single structure anywhere is Dunkirk's
 * `TERR2-28` at **118.3 m**, and the shortest of the conical landforms is
 * **731.5 m** (`TERR5-28`). Paris's `TERR4-59` at 170.7 m is graded as a
 * landform, as it was before the correction.
 *
 * 60 m against the current bake would promote every one of those 76.2 m
 * churches to a hill — seven of them in Verdun alone, doubling its hill count
 * and stamping a 100 m flat zone under each.
 */
export const HILL_MIN_HEIGHT_M = 150;

/** Flat radius granted to a hill, as a multiple of its own footprint radius. */
const HILL_FLAT_SCALE = 1.15;

function lodZExtent(lod: BakedModelLodBody): number {
  let max = 0;
  for (let i = 2; i < lod.positions.length; i += 3) max = Math.max(max, lod.positions[i]!);
  return max;
}

/** Longest edge of each water triangle: on a river's quad strip that is the edge running along the channel. */
function waterSegmentsOf(lod: BakedModelLodBody): { ax: number; ay: number; bx: number; by: number }[] {
  const out: { ax: number; ay: number; bx: number; by: number }[] = [];
  for (let t = 0; t < lod.triangleCount; t++) {
    const face = lod.faceColors[t];
    if (!face || !isWaterColour(face.colorRgba)) continue;
    const corner = (k: number): [number, number] => {
      const v = lod.indices[t * 3 + k]!;
      return [lod.positions[v * 3]!, lod.positions[v * 3 + 1]!];
    };
    const p = [corner(0), corner(1), corner(2)];
    let best = -1;
    let bestLen = -1;
    for (let e = 0; e < 3; e++) {
      const [x0, y0] = p[e]!;
      const [x1, y1] = p[(e + 1) % 3]!;
      const len = (x1 - x0) ** 2 + (y1 - y0) ** 2;
      if (len > bestLen) {
        bestLen = len;
        best = e;
      }
    }
    const [ax, ay] = p[best]!;
    const [bx, by] = p[(best + 1) % 3]!;
    out.push({ ax, ay, bx, by });
  }
  // Zero-width ribbons are stored as 2-point line faces too (world-terrain.md
  // §7.3 — 40 corpus slots have no triangle at any LOD), and those are pure
  // centre-lines, which is exactly what a distance field wants. `lines.colors`
  // is one entry per *segment*, matching `polygon-outlines.ts`'s reading.
  const lines = lod.lines;
  if (lines) {
    const segmentCount = Math.floor(lines.indices.length / 2);
    for (let segment = 0; segment < segmentCount; segment++) {
      const colour = lines.colors[segment]?.colorRgba;
      if (!colour || !isWaterColour(colour)) continue;
      const a = lines.indices[segment * 2]!;
      const b = lines.indices[segment * 2 + 1]!;
      out.push({
        ax: lines.positions[a * 3]!,
        ay: lines.positions[a * 3 + 1]!,
        bx: lines.positions[b * 3]!,
        by: lines.positions[b * 3 + 1]!,
      });
    }
  }
  return out;
}

/** Model frame `(x, y)` at a placement → world `(X, Z)`: the `y -> -Z` flip plus the placement's own yaw and offset. */
function toWorld(
  ax: number,
  ay: number,
  cos: number,
  sin: number,
  px: number,
  pz: number,
): [number, number] {
  const wx = ax;
  const wz = -ay;
  return [cos * wx + sin * wz + px, -sin * wx + cos * wz + pz];
}

export interface ReliefSource {
  readonly options: ReliefOptions;
  /**
   * The aerodrome subset of `options.flatZones` — hills are graded flat too,
   * and stamping a hillside as prepared ground would tell the undercarriage it
   * can be landed on. Only these become `LandingField`s.
   */
  readonly aerodromeZones: readonly FlatZone[];
  /** Diagnostics for the write-up and for the tests that pin them. */
  readonly riverSegmentCount: number;
  readonly hillCount: number;
  /** Aerodromes actually graded — fields whose apron came out wet are dropped, so this can be short of the region's list. */
  readonly aerodromeCount: number;
}

/**
 * Reads a loaded region into `ReliefOptions`, or `undefined` if the region
 * carries a sea plate (see the module doc). `origin` must be the same
 * recentring the renderer and the ground query use.
 *
 * `models` maps `objectRef` to its loaded body; the caller supplies it because
 * `three/world-instancer.ts` is loading the identical set and
 * `loadBakedModel`'s promise cache makes the second read free.
 */
export function reliefSourceFrom(
  region: WorldRegionBody,
  models: ReadonlyMap<string, BakedModelBody>,
  origin: PlacementOrigin,
  /** Already anchored and already recentred by `origin` — `world/region-ground.ts`. */
  aerodromes: readonly (GroundSite & { readonly name: string })[] = [],
  water: WaterTest = NO_WATER,
): ReliefSource | undefined {
  const riverSegments: RiverSegment[] = [];
  const flatZones: FlatZone[] = [];
  let hillCount = 0;

  for (const placement of region.placements) {
    const ref = placement.objectRef;
    if (ref === null) continue;
    const body = models.get(ref);
    if (!body) continue;
    const lod = bestDrawableLod(body.lods);
    if (!lod) continue;

    const { position, yawRadians } = placementWorldTransform(placement, origin);
    const cos = Math.cos(yawRadians);
    const sin = Math.sin(yawRadians);

    if (lodZExtent(lod) >= HILL_MIN_HEIGHT_M) {
      hillCount++;
      flatZones.push({ x: position.x, z: position.z, radiusM: body.boundingRadius * HILL_FLAT_SCALE });
      continue;
    }
    // A sea plate disqualifies the whole region, not just its own faces.
    if (
      (body.scaleExponent ?? 0) >= SEA_SCALE_EXPONENT &&
      lod.faceColors.some((face) => isWaterColour(face.colorRgba))
    ) {
      return undefined;
    }

    for (const seg of waterSegmentsOf(lod)) {
      const [x0, z0] = toWorld(seg.ax, seg.ay, cos, sin, position.x, position.z);
      const [x1, z1] = toWorld(seg.bx, seg.by, cos, sin, position.x, position.z);
      riverSegments.push({ x0, z0, x1, z1 });
    }
  }

  // A wet field is dropped, not graded: `withLandingFields` would otherwise
  // tell the undercarriage that the sea is a prepared strip.
  const aerodromeZones: FlatZone[] = aerodromes
    .filter((field) => dryShare(water, field.x, field.z, AERODROME_GROUND_RADIUS_M) >= 1)
    .map((field) => ({ x: field.x, z: field.z, radiusM: AERODROME_FLAT_M }));
  flatZones.push(...aerodromeZones);

  return {
    options: { seed: region.region, ...DEFAULT_RELIEF, flatZones, riverSegments },
    aerodromeZones,
    riverSegmentCount: riverSegments.length,
    hillCount,
    aerodromeCount: aerodromeZones.length,
  };
}
